import { useState } from 'react';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { FormResponseErrorType } from '../components/molecules/Form';

type DefaultResponse = {
  url?: string;
  errorType?: string;
};

export type UseFetch = {
  checkPageExists?: boolean;
  delay?: number;
};

function defaultValidateStatus(status: number) {
  return status >= 200 && status < 300;
}
/** Hook for handling API requests and managaging some commonly used state */
export function useFetch({ checkPageExists = false, delay = 0 }: UseFetch = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const delayPromise = new Promise(resolve => {
    setTimeout(resolve, delay);
  });

  /** Handle GET requests */
  async function get<Response extends DefaultResponse = any, Payload = any>(
    url: string,
    config: AxiosRequestConfig<Payload> = {}
  ) {
    config.validateStatus = config.validateStatus || defaultValidateStatus;

    const promises: Promise<AxiosResponse<Response>>[] = [axios.get<Response>(url, config)];
    if (delay) {
      promises.push(delayPromise as Promise<AxiosResponse<Response>>);
    }

    try {
      setIsLoading(true);
      const responses = await Promise.all(promises);
      const response = responses[0];

      if (!config.validateStatus(response.status) && !requestError) {
        setRequestError(response.data.errorType || 'Unknown Error');
      } else if (requestError) {
        setRequestError(null);
      }
      return response;
    } catch {
      if (!requestError) {
        setRequestError('Unknown Error');
      }
    } finally {
      setIsLoading(false);
    }
  }

  /** Handle POST requests (with simplified typings)
   *
   * R is the response type (similar to axios.get)
   * P is optional if you want your payload to be checked
   * */
  async function post<Response extends DefaultResponse = any, Payload = any>(
    url: string,
    data: Payload,
    config: AxiosRequestConfig<Payload> = {}
  ) {
    setIsLoading(true);

    // I don't see the benefit of creating a new object here
    config.validateStatus = config.validateStatus || defaultValidateStatus;
    if (data instanceof FormData) {
      config.headers = {
        ...config.headers,
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      };
    }

    const promises: Promise<AxiosResponse<Response>>[] = [
      axios.post<Response, AxiosResponse<Response>, Payload>(url, data, config),
    ];
    if (delay) {
      promises.push(delayPromise as Promise<AxiosResponse<Response>>);
    }

    try {
      setIsLoading(true);
      const responses = await Promise.all(promises);
      const response = responses[0];

      if (!config.validateStatus(response.status) && !requestError) {
        setRequestError(response.data.errorType || 'Unknown Error');
      } else if (requestError) {
        setRequestError(null);
      }

      if (checkPageExists && config.validateStatus(response.status)) {
        if (response.data?.url) {
          try {
            await axios.get(response.data.url);
          } catch {
            throw new Error(FormResponseErrorType.PAGE_NOT_EXISTS);
          }
        }
      }
      return response;
    } catch {
      if (!requestError) {
        setRequestError('Unknown Error');
      }
    } finally {
      setIsLoading(false);
    }
  }

  return { get, post, requestError, isLoading };
}
